'use client'

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMorse } from '../hooks/useMorse'
import { showToast } from '../components/ui/Toast'
import { MorseGlyph } from '../components/lucky/ProfileGlyphs'
import { useLocale, useT } from '../contexts/LocaleContext'

const MORSE_TO_LETTER: Record<string, string> = {
  '.-': 'A', '-...': 'B', '-.-.': 'C', '-..': 'D', '.': 'E',
  '..-.': 'F', '--.': 'G', '....': 'H', '..': 'I', '.---': 'J',
  '-.-': 'K', '.-..': 'L', '--': 'M', '-.': 'N', '---': 'O',
  '.--.': 'P', '--.-': 'Q', '.-.': 'R', '...': 'S', '-': 'T',
  '..-': 'U', '...-': 'V', '.--': 'W', '-..-': 'X', '-.--': 'Y',
  '--..': 'Z',
}

const DOT_MAX_MS = 220
const LETTER_GAP_MS = 680

function formatClock(date: Date) {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
}

function formatRemain(sec: number) {
  const s = Math.max(0, Math.floor(sec))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m.toString().padStart(2, '0')}:${r.toString().padStart(2, '0')}`
}

export default function MorseCodePage() {
  const navigate = useNavigate()
  const t = useT()
  const { dir } = useLocale()
  const { status, loading, error, start, guess, setError } = useMorse()
  const [word, setWord] = useState('')
  const [buffer, setBuffer] = useState('')
  const [badSignal, setBadSignal] = useState(false)
  const [holding, setHolding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [clock, setClock] = useState(() => formatClock(new Date()))
  const downAt = useRef(0)
  const gapTimer = useRef<number | null>(null)
  const bufferRef = useRef('')
  const autoStarted = useRef(false)

  useEffect(() => {
    const id = window.setInterval(() => setClock(formatClock(new Date())), 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => () => {
    if (gapTimer.current) window.clearTimeout(gapTimer.current)
  }, [])

  useEffect(() => {
    if (!status?.is_window_open || status.session_started || autoStarted.current) return
    autoStarted.current = true
    start()
  }, [status?.is_window_open, status?.session_started, start])

  useEffect(() => {
    if (error) showToast(error, 'error')
  }, [error])

  const commitBuffer = (seq: string) => {
    if (!seq) return
    const letter = MORSE_TO_LETTER[seq]
    if (!letter) {
      setBadSignal(true)
      window.setTimeout(() => setBadSignal(false), 500)
      return
    }
    setWord((prev) => (prev + letter).slice(0, 24))
  }

  const clearGap = () => {
    if (gapTimer.current) {
      window.clearTimeout(gapTimer.current)
      gapTimer.current = null
    }
  }

  const onKeyDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!status?.session_active || busy || downAt.current !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    clearGap()
    downAt.current = performance.now()
    setHolding(true)
  }

  const onKeyUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (downAt.current === 0) return
    event.preventDefault()
    const held = performance.now() - downAt.current
    downAt.current = 0
    setHolding(false)
    const symbol = held < DOT_MAX_MS ? '.' : '-'
    const next = (bufferRef.current + symbol).slice(0, 8)
    bufferRef.current = next
    setBuffer(next)
    gapTimer.current = window.setTimeout(() => {
      const seq = bufferRef.current
      bufferRef.current = ''
      setBuffer('')
      commitBuffer(seq)
    }, LETTER_GAP_MS)
  }

  const flushPending = () => {
    clearGap()
    const seq = bufferRef.current
    if (!seq) return word
    bufferRef.current = ''
    setBuffer('')
    const letter = MORSE_TO_LETTER[seq] || ''
    if (!letter) {
      setBadSignal(true)
      window.setTimeout(() => setBadSignal(false), 500)
      return word
    }
    const next = (word + letter).slice(0, 24)
    setWord(next)
    return next
  }

  const handleGuess = async () => {
    const trimmed = flushPending().trim()
    if (!trimmed) {
      showToast(t('morse.errEnterWord'), 'error')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await guess(trimmed)
      if (result) {
        showToast(result.message, result.correct ? 'success' : 'warning')
        if (result.correct) {
          bufferRef.current = ''
          setWord('')
          setBuffer('')
        }
      }
    } finally {
      setBusy(false)
    }
  }

  const clearEntry = () => {
    clearGap()
    bufferRef.current = ''
    setBuffer('')
    setWord('')
  }

  const backspace = () => {
    clearGap()
    if (bufferRef.current) {
      bufferRef.current = ''
      setBuffer('')
      return
    }
    setWord((prev) => prev.slice(0, -1))
  }

  if (loading && !status) {
    return (
      <div className="lucky-morse mini-app-container pb-20 flex justify-center items-center min-h-screen">
        <div className="lucky-morse__empty">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="lucky-morse mini-app-container pb-20 min-h-screen">
      <div className="lucky-morse__inner">
        <header className="lucky-morse__header">
          <button
            type="button"
            className="lucky-morse__back"
            onClick={() => navigate('/games')}
            aria-label={t('common.back')}
          >
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-morse__header-icon">
            <MorseGlyph size={32} />
          </div>
          <div>
            <h1>{t('morse.title')}</h1>
            <p>{t('morse.subtitle')}</p>
          </div>
        </header>

        {!status?.is_window_open && (
          <section className="lucky-morse__card lucky-morse__closed">
            <p>{t('morse.windowClosedTitle')}</p>
            <span>{t('morse.windowClosedHint')}</span>
          </section>
        )}

        {status?.is_window_open && (
          <>
            <section className="lucky-morse__card lucky-morse__meta">
              <div>
                <span>{t('morse.solved')}</span>
                <strong>{status.solved_count}</strong>
              </div>
              {status.session_active && status.seconds_remaining > 0 && (
                <div>
                  <span>{t('morse.timeLeft')}</span>
                  <strong dir="ltr">{formatRemain(status.seconds_remaining)}</strong>
                </div>
              )}
              <div>
                <span>{t('morse.clock')}</span>
                <strong dir="ltr">{clock}</strong>
              </div>
            </section>

            <section className="lucky-morse__card">
              <h2 className="lucky-morse__title">{t('morse.keyTitle')}</h2>
              <p className="lucky-morse__hint">{t('morse.keyHint')}</p>

              <div className="lucky-morse__keyer" dir="ltr">
                <div className="lucky-morse__readout">
                  <span className="lucky-morse__label">{t('morse.readoutWord')}</span>
                  <strong className="lucky-morse__word">{word || '—'}</strong>
                  <span className={`lucky-morse__buffer ${badSignal ? 'is-bad' : ''}`}>
                    {buffer || '\u00a0'}
                  </span>
                </div>

                <button
                  type="button"
                  className={`lucky-morse__key ${holding ? 'is-down' : ''}`}
                  disabled={!status.session_active || busy}
                  onPointerDown={onKeyDown}
                  onPointerUp={onKeyUp}
                  onPointerCancel={onKeyUp}
                  onContextMenu={(event) => event.preventDefault()}
                >
                  <span>{t('morse.keyLabel')}</span>
                  <em>{t('morse.keyHoldHint')}</em>
                </button>
              </div>

              {status.session_active && (
                <div className="lucky-morse__actions">
                  <button
                    type="button"
                    className="lucky-morse__btn-sec"
                    onClick={backspace}
                    disabled={busy || (!word && !buffer)}
                  >
                    {t('morse.backspace')}
                  </button>
                  <button
                    type="button"
                    className="lucky-morse__cta"
                    onClick={handleGuess}
                    disabled={busy || (!word.trim() && !buffer)}
                  >
                    {busy ? '…' : t('morse.submitGuess')}
                  </button>
                  {(word || buffer) && (
                    <button type="button" className="lucky-morse__clear" onClick={clearEntry}>
                      {t('morse.clearAll')}
                    </button>
                  )}
                </div>
              )}

              {status.is_window_open && !status.session_active && status.session_started && (
                <p className="lucky-morse__ended">{t('morse.sessionEnded')}</p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}
